import { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from './ui';
import {
  Sparkles,
  LayoutDashboard,
  MessagesSquare,
  FolderOpen,
  Bell,
  FileText,
  Users,
  Building2,
  SlidersHorizontal,
  ClipboardList,
  ChevronsLeft,
  Send,
  ScrollText,
  Trash2,
  X,
  type LucideIcon,
} from 'lucide-react';

interface NavItem {
  to: string;
  icon: LucideIcon;
  label: string;
  /**
   * Custom active predicate. Needed for the Registro tab, whose section spans
   * several routes (/inicio, /expedientes, /expedientes/:id) that aren't a single
   * URL prefix; falls back to NavLink's own prefix match when omitted.
   */
  match?: (pathname: string) => boolean;
}
interface NavGroup {
  label: string;
  items: NavItem[];
}

/**
 * Cumplimiento console navigation — a collapsible left rail (desktop) that turns
 * into an off-canvas drawer on mobile. A single `<aside>` serves both: `sm:`
 * classes drive the persistent rail, base classes drive the mobile drawer, so
 * there is exactly one `data-tour="agente-nav"` node for the demo tour to find.
 *
 * Items are grouped (Flujo / Administración) and hidden per role for UX only —
 * the server enforces RBAC. Active state is set by React Router's NavLink, which
 * also emits `aria-current="page"`.
 */
export function ConsoleSidebar({
  roles,
  collapsed,
  onToggleCollapse,
  mobileOpen,
  onCloseMobile,
}: {
  roles: string[];
  collapsed: boolean;
  onToggleCollapse: () => void;
  mobileOpen: boolean;
  onCloseMobile: () => void;
}) {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const isSuperAdmin = roles.includes('super_admin');
  const canSeeUsers = ['tenant_admin', 'compliance_manager', 'auditor'].some((r) => roles.includes(r));
  // "Alcance a clientes" is a document-request feature: gate it to the roles
  // that already send/request documents (compliance_manager, intake_officer).
  const canSeeAlcance = ['compliance_manager', 'intake_officer'].some((r) => roles.includes(r));
  // "Auditoría" reads the append-only audit log — audit.list roles only.
  const canSeeAudit = ['tenant_admin', 'compliance_manager', 'auditor'].some((r) => roles.includes(r));
  // "Papelera" (soft-deleted expedientes) — anyone with expediente.list may view.
  const canSeePapelera = ['tenant_admin', 'compliance_manager', 'intake_officer', 'analyst', 'auditor'].some(
    (r) => roles.includes(r)
  );

  const flujo: NavItem[] = [
    {
      to: '/inicio',
      icon: ClipboardList,
      label: 'Registro',
      match: (p) => p === '/inicio' || p.startsWith('/expedientes'),
    },
    { to: '/agente', icon: Sparkles, label: 'Agente' },
    { to: '/panel', icon: LayoutDashboard, label: 'Panel' },
    { to: '/revision', icon: FolderOpen, label: 'Revisión' },
    { to: '/alertas', icon: Bell, label: 'Alertas' },
    { to: '/documentos', icon: FileText, label: 'Documentos' },
    ...(canSeeAlcance ? [{ to: '/alcance', icon: Send, label: t('alcance.title') }] : []),
  ];
  const administracion: NavItem[] = [
    { to: '/matriz-riesgo', icon: SlidersHorizontal, label: 'Matriz de riesgo' },
    ...(canSeeAudit ? [{ to: '/auditoria', icon: ScrollText, label: 'Auditoría' }] : []),
    ...(canSeePapelera ? [{ to: '/papelera', icon: Trash2, label: 'Papelera' }] : []),
    ...(canSeeUsers ? [{ to: '/usuarios', icon: Users, label: t('nav.users') }] : []),
    ...(isSuperAdmin ? [{ to: '/admin/entidades', icon: Building2, label: t('nav.tenants') }] : []),
    ...(isSuperAdmin
      ? [{ to: '/admin/conversaciones-ia', icon: MessagesSquare, label: t('aiLog.title') }]
      : []),
  ];
  // Hide a group's label entirely when role-gating leaves it empty.
  const groups: NavGroup[] = [
    { label: 'Flujo', items: flujo },
    { label: 'Administración', items: administracion },
  ].filter((g) => g.items.length > 0);

  // Escape closes the mobile drawer; body scroll locks while it's open.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseMobile();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [mobileOpen, onCloseMobile]);

  return (
    <>
      {/* backdrop — mobile only, dims content behind the drawer */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-[45] bg-ink/40 backdrop-blur-sm sm:hidden"
          onClick={onCloseMobile}
          aria-hidden
        />
      )}

      <aside
        aria-label="Navegación de Cumplimiento"
        className={cn(
          'z-50 flex flex-col border-r border-line bg-canvas',
          // mobile: off-canvas drawer, full height, slides in
          'fixed inset-y-0 left-0 w-[17rem] max-w-[85vw] transition-transform duration-300 ease-out motion-reduce:transition-none',
          mobileOpen ? 'translate-x-0 shadow-pop' : '-translate-x-full',
          // desktop: persistent sticky rail; width animates on collapse
          'sm:sticky sm:top-16 sm:z-auto sm:h-[calc(100dvh-4rem)] sm:max-w-none sm:translate-x-0 sm:self-start sm:shadow-none sm:transition-[width]',
          collapsed ? 'sm:w-14' : 'sm:w-60',
        )}
      >
        {/* mobile drawer header with a close affordance */}
        <div className="flex h-14 items-center justify-between border-b border-line px-4 sm:hidden">
          <span className="font-mono text-[11px] font-semibold uppercase tracking-wider text-ink-faint">
            Cumplimiento
          </span>
          <button
            onClick={onCloseMobile}
            aria-label="Cerrar menú"
            className="grid h-9 w-9 place-items-center rounded-full text-ink-soft transition-colors hover:bg-panel hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <X className="h-5 w-5" strokeWidth={2} />
          </button>
        </div>

        <nav className="flex-1 space-y-0.5 overflow-y-auto px-2 py-3 [scrollbar-width:thin]">
          {groups.map((group, gi) => (
            <div key={group.label} className={cn(gi > 0 && 'pt-2')}>
              {/* separator shown when the desktop rail is collapsed (labels hidden) */}
              {gi > 0 && (
                <div className={cn('mx-2 mb-1 border-t border-line', collapsed ? 'hidden sm:block' : 'hidden')} />
              )}
              <p
                className={cn(
                  'px-3 pb-1 pt-2 font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-faint',
                  collapsed && 'sm:hidden',
                )}
              >
                {group.label}
              </p>
              {group.items.map(({ to, icon: Icon, label, match }) => (
                <NavLink
                  key={to}
                  to={to}
                  title={label}
                  data-tour={
                    to === '/agente' ? 'agente-nav' : to === '/inicio' ? 'registro-nav' : undefined
                  }
                  onClick={onCloseMobile}
                  // A matched item (Registro) owns its active state across its
                  // whole section (/inicio + /expedientes*), which isn't a single
                  // URL prefix; others use NavLink's default prefix match.
                  className={({ isActive }) => {
                    const active = match ? match(pathname) : isActive;
                    return cn(
                      'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                      collapsed && 'sm:justify-center sm:px-0',
                      active
                        ? 'bg-primary/[0.07] text-primary-dark'
                        : 'text-ink-soft hover:bg-line/60 hover:text-ink',
                    );
                  }}
                >
                  {({ isActive }) => {
                    const active = match ? match(pathname) : isActive;
                    return (
                      <>
                        {active && (
                          <span
                            className={cn(
                              'absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-r-full bg-primary',
                              collapsed && 'sm:hidden',
                            )}
                            aria-hidden
                          />
                        )}
                        <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={active ? 2 : 1.75} />
                        <span className={cn('truncate', collapsed && 'sm:hidden')}>{label}</span>
                      </>
                    );
                  }}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* collapse toggle — desktop rail only */}
        <div className="hidden border-t border-line p-2 sm:block">
          <button
            onClick={onToggleCollapse}
            aria-label={collapsed ? 'Expandir menú' : 'Contraer menú'}
            aria-expanded={!collapsed}
            title={collapsed ? 'Expandir menú' : 'Contraer menú'}
            className={cn(
              'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold text-ink-soft transition-colors hover:bg-line/60 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
              collapsed && 'justify-center px-0',
            )}
          >
            <ChevronsLeft
              className={cn('h-[18px] w-[18px] shrink-0 transition-transform motion-reduce:transition-none', collapsed && 'rotate-180')}
              strokeWidth={1.75}
            />
            {!collapsed && <span>Contraer</span>}
          </button>
        </div>
      </aside>
    </>
  );
}
